import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {LoaderService} from '../../../../services/loader.service';
import {SeoMetaService} from '../../../../services/seo-meta.service';
import {ToolsService} from '../../../../services/tools.service';
import {UrlBuilderService} from '../../../../services/url-builder.service';
import {WPPostGuideLink} from '../../../../types/WPPostGuideLink';
import {WPPostGuide} from '../../../../types/WPPostGuide';
import {MetaTag} from '../../../../types/MetaTag';
import {WpService} from '../../../../services/wp.service';
import {GuidePuffsPlacementEnum, TaxonomyEnum} from '../../../../enums/WPEnums';
import {Breadcrumb} from '../../../../types/Breadcrumb';
import {RoutesEnums, RoutesNamesEnum} from '../../../../enums/RoutesEnums';
import {WPTag} from '../../../../types/WPTag';
import {GuidePostUrlResolverService} from '../../services/guide-post-url-resolver.service';
import {finalize, map, skipWhile, tap} from 'rxjs/operators';
import {LoaderQueueItem} from '../../../../types/LoaderQueueItem';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {PageViewsGa4ResolverService} from '../../../../services/page-views-ga4-resolver.service';
import {DataLayerParamsPageViewGA4} from '../../../../types/DataLayerParamsPageViewGA4';
import {DataLayerParamsClickGA4} from '../../../../types/DataLayerParamsClickGA4';
import {AdcampaignService} from '../../../../services/adcampaign.service';
import {AppConfigService} from '../../../../services/app-config.service';
import {SearchReloadService} from '../../../../services/search/search-reload.service';
import {SearchService} from '../../../../services/search/search.service';
import {StaticGuideService} from '../../../../services/static-guide.service';
import {StaticSidebarPuffsService} from '../../../../services/static-sidebar-puffs.service';

@Component({
  selector: 'app-guide-post',
  templateUrl: './guide-post.component.html',
  styleUrls: ['./guide-post.component.scss', '../../../../../styles/misc/wp-block-library.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuidePostComponent implements OnInit, OnDestroy {
  public guideData = signal<WPPostGuide>(null);
  public guideTag = signal<WPTag>(null);
  public guideLinks = signal<WPPostGuideLink[]>([]);
  public publishDate = computed<string>(() => {
    return this.guideData() ? this.wpService.getFormattedDate(this.guideData().date, 'full') : null;
  });
  public updateDate = computed<string>(() => {
    return this.guideData() ? this.wpService.getFormattedDate(this.guideData().modified, 'full') : null;
  });
  public breadcrumbs = signal<Breadcrumb[]>([]);
  public dataLayerParamsClickGA4 = signal<DataLayerParamsClickGA4>(null);
  public imageContainerPadding = computed<string>(() => {
    if (this.guideData() && this.guideData().acf.mainImage) {
      return `${this.guideData().acf.mainImage.height / this.guideData().acf.mainImage.width}%`;
    } else {
      return '0';
    }
  });
  public pageType: DLPageTypeEnum = DLPageTypeEnum.GUIDE_PAGE;
  appConfigService = inject(AppConfigService);
  public logoUrl = signal<string>(`${this.appConfigService.appConfig.amazonS3Url}assets/varden-logo.svg`);
  protected readonly GuidePuffsPlacementEnum = GuidePuffsPlacementEnum;
  private pageTitle = signal<string>(null);
  private subscriptions: Subscription[] = [];

  constructor(
    public urlBuilder: UrlBuilderService,
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private router: Router,
    private tools: ToolsService,
    private metaService: SeoMetaService,
    private guideService: StaticGuideService,
    private wpService: WpService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private postUrlResolver: GuidePostUrlResolverService,
    private adCampaignService: AdcampaignService,
    private sidebarPuffsService: StaticSidebarPuffsService,
    private reloadService: SearchReloadService,
    private searchService: SearchService,
  ) {
    effect(() => {
      if (this.guideData()) {
        this.wpService.removeSchema();
        this.wpService.addSchema(null, this.guideData(), null, null, null);
      }
    });
  }

  ngOnInit() {
    this.searchService.softResetState();
    this.onRoutingUpdate();
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.onRoutingUpdate();
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.wpService.removeSchema();
  }

  scrollToTop(): void {
    this.tools.scrollTo();
  }

  public trackByMethod(i: number, el: WPPostGuideLink) {
    return el ? el.id : null;
  }

  private onRoutingUpdate(): void {
    this.reloadService.updateParams(this.route.snapshot.params, this.route.snapshot.queryParams);
    const isRedirected: boolean = this.isRedirectedAfterInitUrlResolverAndUpdateState();
    this.loadGuide(isRedirected);
  }

  private isRedirectedAfterInitUrlResolverAndUpdateState(): boolean {
    const isRedirected: boolean = this.postUrlResolver.cleanUpUrl(this.route.snapshot);
    this.postUrlResolver.resetState();
    this.postUrlResolver.processRouteAndQueryParams(this.route.snapshot.params, this.route.snapshot.queryParams);
    return isRedirected;
  }

  private loadGuide(isRedirected: boolean): void {
    if (!this.guideData() || this.guideData().slug !== this.postUrlResolver.postSlug) {
      const queryId: number = this.loaderService.generateQueueId();
      const queryItem: LoaderQueueItem = {
        id: queryId,
        title: '',
        description: '',
      };
      this.loaderService.addToQueue(queryItem);
      this.subscriptions.push(
        this.guideService
          .GetGuideBySlug(this.postUrlResolver.postSlug)
          .pipe(
            map((result) => (this.filterPost(result) ? result : null)),
            tap((result) => this.conditionalRedirectToGuideList(result)),
            skipWhile((result) => result === null),
            finalize(() => {
              this.loaderService.removeFromQueue(queryId);
            }),
          )
          .subscribe(
            (result) => this.processGuideResults(result, isRedirected),
            (error) => {
              this.postUrlResolver.processFailToGetPostData(
                error,
                `guide post get: ${this.postUrlResolver.postSlug} ${this.postUrlResolver.isPreview}`,
              );
            },
          ),
      );
    }
  }

  private processGuideResults(result: WPPostGuide, isRedirected: boolean): void {
    this.guideData.set(result);
    if (!this.tools.isNil(this.guideData())) {
      this.loadGuideData();
      this.scrollToTop();
      if (isRedirected === false) {
        // this.loadAdCampaignOwnersAndTriggerDataLayerTracking(); ignore assigned taxonomies and ask for campaign ID
        this.loadAdCampaignOwnerAndTriggerDataLayerTracking();
      }
    } else {
      this.postUrlResolver.processFailToGetPostData(
        'Empty post data',
        `guide post get: ${this.postUrlResolver.postSlug} ${this.postUrlResolver.isPreview}`,
      );
    }
  }

  private loadAdCampaignOwnerAndTriggerDataLayerTracking(): void {
    if (!this.tools.isNil(this.guideData().acf.adCampaignId) && this.guideData().acf.adCampaignId.toString() !== '') {
      this.subscriptions.push(
        this.adCampaignService.getAdCampaignOwner(this.guideData().acf.adCampaignId).subscribe(
          (result) => {
            this.triggerDataLayerTracking(result);
          },
          (error) => {
            this.triggerDataLayerTracking(null);
          },
        ),
      );
    } else {
      this.triggerDataLayerTracking(null);
    }
  }

  private triggerDataLayerTracking(adCampaignOwner: number): void {
    const customGa4Params: DataLayerParamsPageViewGA4 = this.wpService.createDataLayerGa4CustomParams(
      this.guideData(),
      DLPageTypeEnum.GUIDE_PAGE,
      this.pageTitle(),
    );
    customGa4Params.adCampaignOwner = adCampaignOwner;
    this.pageViewGa4Resolver.trackPageView(customGa4Params);

    this.dataLayerParamsClickGA4.set(
      this.wpService.createDataLayerGa4ClickCustomParams(
        this.guideData(),
        '',
        DLPageTypeEnum.GUIDE_PAGE,
        this.pageTitle(),
      ),
    );
  }

  private conditionalRedirectToGuideList(result: WPPostGuide): void {
    if (result === null) {
      this.postUrlResolver.processFailToGetPostData('No items to show', 'Preview item filtered out');
    }
  }

  private filterPost(result: WPPostGuide): boolean {
    return (
      this.postUrlResolver.isPreview ||
      (result && !result.acf) ||
      (result && result.acf && !result.acf.isPreview) ||
      (result && result.acf && result.acf.isPreview === false)
    );
  }

  private loadGuideData(): void {
    this.sidebarPuffsService.reloadCombinedPuffs(
      RoutesEnums.GUIDE,
      null,
      this.guideData().care_provider_unit_type,
      null,
      null,
      [this.guideData().id],
    );
    this.loadGuideTagData();
    if (this.guideData().acf.showLinks) {
      this.loadGuideLinks();
    }
    this.updateMetaTags();
  }

  private loadGuideTagData(): void {
    this.subscriptions.push(
      this.wpService.GetTaxonomyItem(TaxonomyEnum.GUIDE_TAG, this.guideData().guidetag[0]).subscribe(
        (result) => this.processTagResults(result),
        (error) => {
          console.error(error);
        },
      ),
    );
  }

  private loadGuideLinks(): void {
    this.subscriptions.push(
      this.guideService.GetGuideLinksByGuideId(this.guideData().id).subscribe(
        (result) => this.processGuideLinksResults(result),
        (error) => {
          console.error(error);
        },
      ),
    );
  }

  private processGuideLinksResults(result: WPPostGuideLink[]): void {
    this.guideLinks.set(result);
  }

  private processTagResults(result: WPTag): void {
    this.guideTag.set(result);
    this.buildBreadcrumbs();
  }

  private buildBreadcrumbs() {
    const breadcrumbs: Breadcrumb[] = [];
    breadcrumbs.push({
      routeParams: ['/', RoutesEnums.GUIDE],
      routeQueryParams: null,
      anchor: RoutesNamesEnum.GUIDE,
    });
    breadcrumbs.push({
      routeParams: ['/', RoutesEnums.GUIDE, RoutesEnums.TAG, this.guideTag().slug],
      routeQueryParams: null,
      anchor: this.guideTag().name,
    });

    breadcrumbs.push({
      routeParams: null,
      routeQueryParams: null,
      anchor: this.guideData().title.rendered,
    });
    this.breadcrumbs.set(breadcrumbs);
  }

  private updateMetaTags(): void {
    const metaTags: MetaTag[] = [];

    if (!this.tools.isNil(this.guideData()) && !this.tools.isNil(this.guideData().acf)) {
      const title: string = this.guideData().acf.seoTitle;
      const description: string = this.guideData().acf.seoDescription;
      const suffix = ' - Vården.se';

      this.pageTitle.set(title + suffix);
      metaTags.push({
        value: title + suffix,
        type: 'title',
        field: 'title',
      });

      metaTags.push({
        value: title + suffix,
        type: 'property',
        field: 'og:title',
      });

      metaTags.push({
        value: 'website',
        type: 'property',
        field: 'og:type',
      });

      metaTags.push({
        value: this.urlBuilder.CreateStaticLink(this.urlBuilder.CreateGuidePostRouterLink(this.guideData().slug)),
        type: 'property',
        field: 'og:url',
      });

      metaTags.push({
        value: description,
        type: 'name',
        field: 'description',
      });

      metaTags.push({
        value: description,
        type: 'property',
        field: 'og:description',
      });

      metaTags.push({
        value: this.appConfigService.appConfig.siteName,
        type: 'property',
        field: 'og:site_name',
      });

      if (!this.tools.isNil(this.guideData().acf.mainImage) && this.guideData().acf.showMainImageOnPostPage) {
        metaTags.push({
          value: this.wpService.getImageFlatUrl(this.guideData().acf.mainImage),
          type: 'property',
          field: 'og:image',
        });

        metaTags.push({
          value: this.wpService.getImageFlatUrl(this.guideData().acf.mainImage),
          type: 'name',
          field: 'twitter:image',
        });

        metaTags.push({
          value: 'summary_large_image',
          type: 'name',
          field: 'twitter:card',
        });
      }

      this.metaService.updateMetaTags(metaTags);
    }
  }
}
